import 'bootstrap/dist/css/bootstrap.css';
import { useEffect, useState } from 'react';
import Spinner from '../../../shared/spinner';
import { format, parse, parseISO } from 'date-fns';
import { OperacaoParcela } from '../../../shared/model/operacaoParcela';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faClose, faCalculator, faSearch } from '@fortawesome/free-solid-svg-icons';
import InputMask from 'react-input-mask';
import DateInput from '../../../shared/dateInput';
import { BaixaParcelaDTO } from '../../../shared/dto/baixaParcelaDTO';
import { MovimentoFinanceiroEnum } from '../../../shared/constantes/movimentoFinanceiroEnum';
import { HistoricoFinanceiro } from '../../../shared/model/historicoFinanceiro';
import OperacaoParcelaService from '../../../services/operacaoParcelaService';
import HistoricoFinanceiroService from '../../../services/historicoFinanceiroService';
import CustomAlert from '../../../shared/customAlert';
import { getParcelaQuitacaoKey } from '../../../shared/parcelasQuitacao';
import { calcularTotaisBaixaFinanceira } from '../../../shared/parcelasBaixaFinanceira';

const formatarValorMoeda = (valor: number | null | undefined): string => {
  return (valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

function BaixaParcelaFinanceiraManualComponent() {
  const [alert, setAlert] = useState<{ message: string, type: 'success' | 'warning' | 'error' } | null>(null);
  const [loading, setLoading] = useState(false);

  const [selectFormaPagamento, setSelectFormaPagamento] = useState('SALDO_COM_DESCONTO');
  const [baixaDto, setBaixaDto] = useState<BaixaParcelaDTO>();
  const [parcelasList, setParcelasList] = useState<OperacaoParcela[] | null>([]);
  const [parcelasSelecionadas, setParcelasSelecionadas] = useState<string[]>([]);
  const [exibeDetalhe, setExibeDetalhe] = useState(false);

  const [historicoFinanceiro, setHistoricoFinanceiro] = useState('');
  const [historicoFinanceiroList, setHistoricoFinanceiroList] = useState<HistoricoFinanceiro[]>([]);

  const [contrato, setContrato] = useState('');
  const [dtMovimento, setDtMovimento] = useState(format(parseISO(new Date().toISOString()), 'dd/MM/yyyy'));
  const [nomeCliente, setNomeCliente] = useState('');
  const [faixaParcela1, setFaixaParcela1] = useState('');
  const [faixaParcela2, setFaixaParcela2] = useState('');
  const [valorPago, setValorPago] = useState<number>(0);
  const [valorPagoInput, setValorPagoInput] = useState<number>(0);
  const [confirmarBaixaValorInferior, setConfirmarBaixaValorInferior] = useState(false);

  const movimento = MovimentoFinanceiroEnum;

  const operacaoParcelaService: OperacaoParcelaService = new OperacaoParcelaService();
  const historicoFinanceiroService: HistoricoFinanceiroService = new HistoricoFinanceiroService();

  const parcelasSelecionadasList = (parcelasList ?? []).filter((item) =>
    parcelasSelecionadas.includes(getParcelaQuitacaoKey(item))
  );
  const totaisParcelasSelecionadas = calcularTotaisBaixaFinanceira(parcelasSelecionadasList);
  const todasParcelasSelecionadas = parcelasList != null
    && parcelasList.length > 0
    && parcelasSelecionadasList.length === parcelasList.length;

  const getValorPagoPorMovimento = (movimentoSelecionado: string, totais = totaisParcelasSelecionadas) => {
    if (movimentoSelecionado === 'SALDO_COM_DESCONTO' || movimentoSelecionado === 'DISTRIBUIR_CONF_SALDO') {
      return totais.saldoDevedor;
    }

    if (movimentoSelecionado === 'VALOR_FINAL_PARCELA' || movimentoSelecionado === 'DISTRIBUIR_CONF_VALOR_FINAL') {
      return totais.valorBruto;
    }

    return 0;
  };

  const getValorPagoDigitado = () => {
    return parseFloat(valorPagoInput.toString().replace(',', '.')) || 0;
  };

  const isMovimentoDistribuicaoValorDigitado = () => {  
    return selectFormaPagamento === 'DISTRIBUIR_CONF_SALDO'
      || selectFormaPagamento === 'DISTRIBUIR_CONF_VALOR_FINAL';
  };

  const getTotalEsperadoParaBaixa = () => {
    return getValorPagoPorMovimento(selectFormaPagamento);
  };

  const atualizarValorPagoSelecionado = (selecionadas: string[], movimentoSelecionado = selectFormaPagamento) => {
    const parcelasSelecionadasAtualizadas = (parcelasList ?? []).filter((item) =>
      selecionadas.includes(getParcelaQuitacaoKey(item))
    );
    const totais = calcularTotaisBaixaFinanceira(parcelasSelecionadasAtualizadas);
    const valor = getValorPagoPorMovimento(movimentoSelecionado, totais);

    setValorPago(valor);
    setValorPagoInput(valor);
  };

  const selecionarTodasParcelas = (marcar: boolean) => {
    const selecionadas = marcar && parcelasList ? parcelasList.map(getParcelaQuitacaoKey) : [];
    setParcelasSelecionadas(selecionadas);
    atualizarValorPagoSelecionado(selecionadas);
  };

  const selecionarParcela = (parcela: OperacaoParcela, marcar: boolean) => {
    const key = getParcelaQuitacaoKey(parcela);

    setParcelasSelecionadas((selecionadas) => {
      const novasSelecionadas = marcar
        ? (selecionadas.includes(key) ? selecionadas : [...selecionadas, key])
        : selecionadas.filter((item) => item !== key);

      atualizarValorPagoSelecionado(novasSelecionadas);
      return novasSelecionadas;
    });
  };

  const listarContrato = async () => {
    if (contrato === '') return setAlert({ message: 'Preencha o campo de busca', type: 'warning' });
    try {
      setParcelasList([]);
      setLoading(true);

      const responseList = await operacaoParcelaService.listarParcelasParaBaixaFinanceiras(contrato, new BaixaParcelaDTO());
      const json = responseList.data;
      setBaixaDto(json);
      setParcelasList(null);
      setParcelasSelecionadas([]);
      //setParcelasList(json.parcelas);
      setNomeCliente(json.nomeCliente);
      setFaixaParcela1(json.faixaParcela1?.toString() ?? '');
      setFaixaParcela2(json.faixaParcela2?.toString() ?? '');
      setValorPago(json.saldoTotal);
      setValorPagoInput(json.saldoTotal);

      if (json.bloqueioPropostaEsteira !== null) {
        setAlert({ message: 'Não é possível baixar parcelas deste contrato, proposta - ' + json.bloqueioPropostaEsteira + ' - de REFIN na esteira.', type: 'warning' });
      }

      setExibeDetalhe(true);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setAlert({ message: 'Não há pagamentos pendentes de baixa financeira para este contrato.', type: 'warning' });
      console.error(err);
    }
  }

  const listarHistoricoFinanceiro = async () => {
    try {
      setLoading(true);
      const responseList = await historicoFinanceiroService.listarHistoricoFinanceiroPorTipoEAtivo();
      const json = responseList.data;
      setHistoricoFinanceiroList(json);
      setHistoricoFinanceiro(json[0].id!.toString());
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error(err);
    }
  }

  const listarParcelas = async () => {
    if (contrato === '') return setAlert({ message: 'Preencha o campo de busca', type: 'warning' });
    if (parse(dtMovimento, "dd/MM/yyyy", new Date()) > new Date()) return setAlert({ message: 'Data de movimento não pode ser superior a data de hoje', type: 'warning' });
    try {
      setParcelasList([]);
      setLoading(true);
      let dto: BaixaParcelaDTO = new BaixaParcelaDTO();
      dto.faixaParcela1 = parseInt(faixaParcela1);
      dto.faixaParcela2 = parseInt(faixaParcela2);
      dto.dtMovimento = parse(dtMovimento, "dd/MM/yyyy", new Date());

      const responseList = await operacaoParcelaService.listarParcelasParaBaixaFinanceiras(contrato, dto);
      const json = responseList.data;
      const parcelasCalculadas = json.parcelas || [];
      const parcelasSelecionadasPadrao = parcelasCalculadas.map(getParcelaQuitacaoKey);
      const totaisSelecionados = calcularTotaisBaixaFinanceira(parcelasCalculadas);
      const valorPagoSelecionado = getValorPagoPorMovimento(selectFormaPagamento, totaisSelecionados);

      setBaixaDto(json);
      setParcelasList(parcelasCalculadas);
      setParcelasSelecionadas(parcelasSelecionadasPadrao);
      setNomeCliente(json.nomeCliente);
      setFaixaParcela1(json.faixaParcela1?.toString() ?? '');
      setFaixaParcela2(json.faixaParcela2?.toString() ?? '');
      setValorPago(valorPagoSelecionado);
      setValorPagoInput(valorPagoSelecionado);

      if (json.bloqueioPropostaEsteira !== null) {
        setAlert({ message: 'Não é possível baixar parcelas deste contrato, proposta - ' + json.bloqueioPropostaEsteira + ' - de REFIN na esteira.', type: 'warning' });
      }

      setExibeDetalhe(true);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error(err);
    }
  }

  const executarBaixaParcelas = async (confirmarValorPagoInferiorTotal = false) => {
    if (parse(dtMovimento, "dd/MM/yyyy", new Date()) > new Date()) return setAlert({ message: 'Data de movimento não pode ser superior a data de hoje', type: 'warning' });
    try {
      if (parcelasSelecionadasList.length === 0) return setAlert({ message: 'Selecione ao menos uma parcela para baixa', type: 'warning' });

      setLoading(true);
      let dto: BaixaParcelaDTO = new BaixaParcelaDTO();
      dto.faixaParcela1 = parseInt(faixaParcela1);
      dto.faixaParcela2 = parseInt(faixaParcela2);
      dto.dtMovimento = parse(dtMovimento, "dd/MM/yyyy", new Date());
      dto.valorMovimento = getValorPagoDigitado();
      dto.movimento = selectFormaPagamento;
      dto.parcelas = parcelasSelecionadasList;
      dto.idHistoricoFinanceiro = historicoFinanceiro === '' ? null : parseInt(historicoFinanceiro);
      dto.bloqueioPropostaEsteira = baixaDto?.bloqueioPropostaEsteira!;
      dto.confirmarValorPagoInferiorTotal = confirmarValorPagoInferiorTotal;

      await operacaoParcelaService.baixarParcelasFinanceiras(contrato, dto);
      setLoading(false);
      setAlert({ message: 'Parcelas baixadas com sucesso', type: 'success' });

      limpaDados();
    } catch (err: any) {
      setLoading(false);
      const mensagemErro = err.response?.data || 'Ocorreu um erro ao baixar parcelas';
      if (mensagemErro.includes('O valor pago informado é inferior ao total')) {
        setConfirmarBaixaValorInferior(true);
      } else {
        setAlert({ message: mensagemErro, type: 'error' });
      }
      console.error(err);
    }
  }

  const baixarParcelas = async () => {
    if (parcelasSelecionadasList.length === 0) return setAlert({ message: 'Selecione ao menos uma parcela para baixa Financeira', type: 'warning' });

    if (isMovimentoDistribuicaoValorDigitado()
      && getValorPagoDigitado() + 0.000001 < getTotalEsperadoParaBaixa()) {
      setConfirmarBaixaValorInferior(true);
      return;
    }

    await executarBaixaParcelas(false);
  }

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value;
    setSelectFormaPagamento(selectedValue);
    const valor = getValorPagoPorMovimento(selectedValue);
    setValorPago(valor);
    setValorPagoInput(valor);
  };

  const handleInputChange = (e: any) => {
    let value = e.target.value;
    value = value.replace(/\D/g, '');
    if (value) {
      value = (parseFloat(value) / 100).toFixed(2);
      value = value.replace('.', ',');
    }
    setValorPagoInput(value);
  };

  const limpaDados = () => {
    setExibeDetalhe(false);
    setNomeCliente('');
    setFaixaParcela1('');
    setFaixaParcela2('');
    setValorPago(0);
    setParcelasList([]);
    setParcelasSelecionadas([]);
  }

  useEffect(() => {
    listarHistoricoFinanceiro();
    const today = new Date();
    const formattedDate = today.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }, []);

  return (
    <>
      {alert && (<CustomAlert message={alert.message} type={alert.type} onClose={() => setAlert(null)} />)}
      {confirmarBaixaValorInferior && (
        <CustomAlert
          message={'O valor pago informado é inferior ao total das parcelas selecionadas.\nDeseja continuar com a operação?'}
          type="warning"
          onClose={() => setConfirmarBaixaValorInferior(false)}
          onCancel={() => setConfirmarBaixaValorInferior(false)}
          onConfirm={() => {
            setConfirmarBaixaValorInferior(false);
            executarBaixaParcelas(true);
          }}
          confirmLabel="Sim, continuar"
          cancelLabel="Não"
        />
      )}
      <div className="position-relative m-md-3">
        {loading ? <Spinner loading={loading} /> : (
          <>
            <div className="card" style={{ marginTop: '25px' }}>
              <div className="card-body">
                <div className="card-header">
                  <strong>Baixa de Parcela</strong>
                </div>

                <div className="row" style={{ marginTop: '10px' }}>
                  <div className="col-md-2">
                    <div className="form-group">
                      <label>Contrato <span style={{ color: 'red' }}>*</span></label>
                      <input type="text" className="form-control form-control-sm brds " style={{ color: 'Black', backgroundColor: 'White' }}
                        value={contrato} onChange={(e) => { setContrato((e.target.value)); limpaDados() }} />
                    </div>
                  </div>
                  <div className="col-md-2">
                    <button className="input-group-btn"
                      style={{
                        backgroundColor: 'white', width: '30%', marginLeft: '10px', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', border: '1px solid LightGrey', borderRadius: '5px', height: '54px', minWidth: '50px'
                      }} onClick={listarContrato}>
                      <a >
                        <FontAwesomeIcon icon={faSearch} style={{ color: 'black' }} />
                      </a>
                    </button>
                  </div>
                </div>

                {exibeDetalhe ? (
                  <>
                    <div className="row" style={{ marginTop: '10px' }}>
                      <div className="col-md-3">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>Nome Cliente</span>
                          <input disabled type="text" value={nomeCliente} className="form-control form-control-sm brds " style={{ color: 'Black', backgroundColor: 'LightGrey' }} />
                        </div>
                      </div>
                    </div>
                    <div className="row" style={{ marginTop: '10px' }}>
                      <div className="col-md-8">
                        <div className="col-md-4">
                          <label>Faixa de Parcela <span style={{ color: 'red' }}>*</span></label>
                          <thead>
                            <tr>
                              <th style={{ width: "150px" }}><InputMask required maskChar="" mask="999" type="text" className="form-control" value={faixaParcela1} onChange={(e) => { setFaixaParcela1((e.target.value).replace(/_/g, "")); setParcelasList(null); setParcelasSelecionadas([]); }} /></th>
                              <th style={{ width: "15px" }}> a </th>
                              <th style={{ width: "150px" }}><InputMask required maskChar="" mask="999" type="text" className="form-control" value={faixaParcela2} onChange={(e) => { setFaixaParcela2((e.target.value).replace(/_/g, "")); setParcelasList(null); setParcelasSelecionadas([]); }} /></th>
                            </tr>
                          </thead>
                        </div>
                      </div>
                    </div>
                    <div className="" style={{ marginTop: '10px' }}>
                      <div className="row">
                        <div className="col-md-2" style={{ paddingLeft: '10px', paddingRight: '10px' }}>
                          <label style={{ width: '100%' }}>Data Movimento</label>
                        </div>
                      </div>
                      <div className="row" style={{ marginTop: '5px' }}>
                        <div className="col-md-2" style={{ paddingLeft: '10px' }}>
                          <DateInput required className="form-control" type="text"
                            value={dtMovimento} onChange={(e) => setDtMovimento((e.target.value).replace(/_/g, ""))} />
                        </div>
                      </div>
                    </div>
                    <div className="row" style={{ marginTop: '15px' }}>
                      <div className="col-md-3">
                        <button className="input-group-btn" id="App-button-cor-principal"
                          style={{
                            width: '40%', display: 'flex', alignItems: 'center',
                            justifyContent: 'center', border: '1px solid LightGrey', borderRadius: '5px', height: '50px', minWidth: '50px'
                          }} onClick={listarParcelas}>
                          <a>
                            <FontAwesomeIcon icon={faCalculator} style={{ color: 'White', fontSize: 'larger', marginTop: '5px' }} />
                          </a>
                          <strong style={{ color: 'White', marginLeft: '5px', fontSize: 'larger' }}> Calcular</strong>
                        </button>
                      </div>
                    </div>
                    {parcelasList != null && parcelasList!.length > 0 ? (
                      <div className="table-responsive">
                        <div style={{ width: '100%', borderWidth: '1px', marginTop: '10px' }}>
                          <table cellSpacing="2" cellPadding="4" rules="all" style={{ border: '3', color: 'Black', borderColor: '#999999', borderWidth: '3px', borderStyle: 'Solid', width: '100%' }} className="table table-bordered table-striped">
                            <tbody>
                              <tr className="GridHeaderStyle" style={{ backgroundColor: 'white', color: 'black' }}>
                                <th style={{ textAlign: 'center' }} scope="col" id="App-header-table">
                                  <input
                                    type="checkbox"
                                    checked={todasParcelasSelecionadas}
                                    onChange={(e) => selecionarTodasParcelas(e.target.checked)}
                                    aria-label="Selecionar todas as parcelas"
                                  /> Tudo
                                </th>
                                <th style={{ textAlign: 'center' }} scope="col" id="App-header-table">NÚMERO</th>
                                <th style={{ textAlign: 'center' }} scope="col" id="App-header-table">VENCIMENTO</th>
                                <th style={{ textAlign: 'center' }} scope="col" id="App-header-table">STATUS</th>
                                <th style={{ textAlign: 'center' }} scope="col" id="App-header-table">ATRASO</th>
                                <th style={{ textAlign: 'center' }} scope="col" id="App-header-table">VALOR PARCELA</th>
                                <th style={{ textAlign: 'center' }} scope="col" id="App-header-table">DESCONTO</th>
                                <th style={{ textAlign: 'center' }} scope="col" id="App-header-table">VALOR PAGO</th>
                              </tr>
                              {parcelasList.map((item) => (
                                <tr className="GridRowStyle" key={getParcelaQuitacaoKey(item)}>
                                  <td style={{ textAlign: 'center' }}>
                                    <input
                                      type="checkbox"
                                      checked={parcelasSelecionadas.includes(getParcelaQuitacaoKey(item))}
                                      onChange={(e) => selecionarParcela(item, e.target.checked)}
                                      aria-label={`Selecionar parcela ${item.numeroParcela || ''}`}
                                    />
                                  </td>
                                  <td style={{ textAlign: 'center' }}>{item.numeroParcela}</td>
                                  <td style={{ textAlign: 'center' }}>{item.dtVencimento ? format(parseISO(item.dtVencimento.toString()), 'dd/MM/yyyy') : ''}</td>
                                  <td style={{ textAlign: 'center' }}>{item.tipoMovimentacao !== null ? (item.tipoMovimentacao !== 'BAIXA_PARCIAL' ? 'BAIXA_TOTAL' : item.tipoMovimentacao)?.replace(/_/g, ' ') : ''}</td>
                                  <td style={{ textAlign: 'center' }}>{item.atraso ? `${item.atraso}` : '0'}</td>
                                  <td style={{ textAlign: 'center' }}>
                                    R$ {formatarValorMoeda(item.valorParcela)}
                                  </td>
                                  <td style={{ textAlign: 'center' }}>R$ {item.desconto !== null ? item.desconto!.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0,00'}</td>
                                  <td style={{ textAlign: 'center' }}>R$ {formatarValorMoeda(item.valorPago ?? 0)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div className="row" style={{ marginTop: '10px' }}>
                          <div className="col-md-3">
                            <div className="form-group">
                              <span style={{ textAlign: 'left' }}>Valor Final</span>
                              <input type="text" value={'R$ ' + formatarValorMoeda(totaisParcelasSelecionadas.valorBruto)} className="form-control form-control-sm brds " style={{ color: 'Black', backgroundColor: 'LightGrey' }} />
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="form-group">
                              <span style={{ textAlign: 'left' }}>Total a converter</span>
                              <input type="text" value={'R$ ' + formatarValorMoeda(totaisParcelasSelecionadas.saldoDevedor)} className="form-control form-control-sm brds " style={{ color: 'Black', backgroundColor: 'LightGrey' }} />
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="form-group">
                              <label>Valor Pago</label>
                              <input type="text" className="form-control form-control-sm brds "
                                value={valorPagoInput.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} onChange={handleInputChange}
                                readOnly={selectFormaPagamento === 'SALDO_COM_DESCONTO' || selectFormaPagamento === 'VALOR_FINAL_PARCELA'}
                                style={selectFormaPagamento === 'SALDO_COM_DESCONTO' || selectFormaPagamento === 'VALOR_FINAL_PARCELA' ? { color: 'Black', backgroundColor: 'LightGrey' } : { color: 'Black', backgroundColor: 'White' }}
                              />
                            </div>
                          </div>
                        </div>
                        {baixaDto?.bloqueioPropostaEsteira === null && (
                          <div className="row" style={{ marginTop: '10px', height: '50px', maxWidth: '70%' }}>
                            <div className="col-md-3">
                              <button className="input-group-btn" id="App-button-cor-principal"
                                style={{
                                  width: '80%', display: 'flex', alignItems: 'center',
                                  justifyContent: 'center', border: '1px solid LightGrey', borderRadius: '5px', height: '100%', minWidth: '50px'
                                }} onClick={baixarParcelas}>
                                <a>
                                  <FontAwesomeIcon icon={faSave} style={{ color: 'White', fontSize: 'larger', marginTop: '5px' }} />
                                </a>
                                <strong style={{ color: 'White', marginLeft: '5px', fontSize: 'larger' }}> Confirmar Baixa</strong>
                              </button>
                            </div>
                            <div className="col-md-3">
                              <button className="input-group-btn"
                                style={{
                                  width: '80%', display: 'flex', alignItems: 'center', backgroundColor: 'red', borderColor: 'red',
                                  justifyContent: 'center', border: '1px solid LightGrey', borderRadius: '5px', height: '100%', minWidth: '50px'
                                }} onClick={() => window.history.back()}>
                                <a>
                                  <FontAwesomeIcon icon={faClose} style={{ color: 'White', fontSize: 'larger', marginTop: '5px' }} />
                                </a>
                                <strong style={{ color: 'White', marginLeft: '5px', fontSize: 'larger' }}> Cancelar</strong>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : parcelasList != null ? (
                      <div style={{ width: '100%', borderWidth: '1px', marginTop: '10px' }}>
                        <span style={{ color: 'red' }}>Não há pagamentos pendentes de baixa financeira para este contrato.</span>
                      </div>
                    ) : null}

                  </>
                ) : null}
              </div>
            </div>
          </>
        )}
      </div >
    </>
  );
}

export default BaixaParcelaFinanceiraManualComponent;
